import { useSelector } from 'react-redux';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CustomNotification, dialogFunction } from 'common/components';

import selectorsGridView from 'common/components/grid-view/controllers/selectors';

import * as creditCardServices from 'services/creditCardEula';
import * as productServices from 'services/product';
import {
  useRefetchAllMemberBillingStatement,
  useQueryMemberDiscountProperties,
  useQueryBillingNote,
} from 'pages/company-profile/components/tabs/billing/queries';
import { useGetMemberId } from 'hooks';
import { useIXOneCapture } from '../ixone-credit-card/IXOneContext';
import { getAllIXOneRows, recalculateFee } from './utils';
import { sleep } from 'utils/delay';
import { isObject } from 'pages/product-full-view/components/product-new-detail-view/utils';

export const captureProductsQueryKey = (productIds) => [
  'credit-card',
  'products-for-capture',
  ...productIds,
];

export const mappingProductList = (productList) => {
  return productList?.map((product) => {
    return {
      ...product,
      initFee: product.fee,
    };
  });
};

export const useGetProductsForCapture = ({
  productIds,
  ...otherProps
} = {}) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: captureProductsQueryKey(productIds),
    queryFn: async () => {
      const response = await creditCardServices.getProductsForCapture({
        productIds,
      });

      const { isSuccess, message } = response;

      if (!isSuccess) {
        const error = message || 'Server Error';
        CustomNotification.error(error);
      }

      return Promise.resolve(response);
    },
    refetchOnWindowFocus: false,
    enabled: Boolean(productIds?.length > 0),
    staleTime: 0,
    ...otherProps,
  });

  const handleRefetchProductsForCapture = (productIds) => {
    queryClient.invalidateQueries({
      queryKey: captureProductsQueryKey(productIds),
    });
  };

  return {
    productList: mappingProductList(query?.data?.data?.verifiedProducts) ?? [],
    failedProductList: query?.data?.data?.failedProducts ?? [],
    handleRefetchProductsForCapture,
    ...query,
  };
};

export const useMutationCreateProduct = () => {
  return useMutation({
    mutationFn: productServices.addProduct,
    onSuccess: (response) => {
      if (response?.isSuccess) {
        CustomNotification.success('Create product successfully');
      } else {
        CustomNotification.error(response?.message ?? 'Something went wrong');
      }
    },
    onError: () => {
      CustomNotification.error('Failed to create new product');
    },
  });
};

export const mutationSendIXOneCaptureKeys = ['payment', 'send-ixone-capture'];

export const useMutationSendProductToCapture = ({
  onCancel,
  ...props
} = {}) => {
  const refetchAllMemberBillingStatement =
    useRefetchAllMemberBillingStatement();

  const { memberId } = useGetMemberId();

  const { invalidateMemberDiscountProperties } =
    useQueryMemberDiscountProperties({ memberId, enabled: false });
  const { invalidateBillingNote } = useQueryBillingNote({
    memberId,
    enabled: false,
  });

  return useMutation({
    mutationKey: mutationSendIXOneCaptureKeys,
    mutationFn: creditCardServices.sendProductToCapture,
    onSuccess: (response) => {
      if (response?.isSuccess) {
        CustomNotification.success('Send product(s) to capture successfully');
      } else {
        const errorMsg = response?.message ?? 'Something went wrong';
        dialogFunction({
          type: 'warn',
          content: errorMsg,
          okText: 'OK',
          cancelButtonProps: { style: { display: 'none' } },
          onOk: () => {
            onCancel?.(); //* close charge dialog
          },
        });
      }
      refetchAllMemberBillingStatement();
      invalidateMemberDiscountProperties();
      invalidateBillingNote();
    },
    onError: () => {
      CustomNotification.error('Failed to send product(s) to capture');
    },
    ...props,
  });
};

export const useGetSelectedProductIdsGrid = () => {
  const selectProductGrid = useSelector(
    selectorsGridView.makeSelectDetailCurrentITemsSelection()
  );

  const selectedProductIdsGrid =
    selectProductGrid?.map((product) => parseInt(product?.id)) ?? [];

  return selectedProductIdsGrid;
};

export const useHierarchyAppliedProduct = () => {
  const { selectedHierarchies, ixoneGridApi, onSelectHierarchy } =
    useIXOneCapture();

  const applySelectedHierarchyForProduct = ({
    allRowData,
    selectedHierarchy,
    productId,
  }) => {
    const isSelectHierarchy =
      isObject(selectedHierarchy) && Object.keys(selectedHierarchy).length > 0;

    onSelectHierarchy({
      hierarchyValue: selectedHierarchy,
      productId: productId,
    });

    const newRowData = recalculateFee({
      productId: productId,
      hierarchyValue: selectedHierarchy,
      allRowData,
      isSelectHierarchy,
    });

    ixoneGridApi.api.applyTransaction({
      update: newRowData,
    });
  };

  const applySelectedHierarchyForMultiProduct = async () => {
    if (!ixoneGridApi?.api) return;

    await sleep(200);
    const allRowData = getAllIXOneRows(ixoneGridApi);

    if (allRowData.length === 0) return;

    const updates = [];

    Object.entries(selectedHierarchies).forEach(
      ([productIdStr, hierarchyInfo]) => {
        const productId = Number(productIdStr);
        const product = allRowData.find(
          (p) => Number(p.productId) === productId
        );

        if (!product) return;

        const selectedHierarchy = hierarchyInfo?.selectedHierarchy;
        const hasHierarchy =
          selectedHierarchy && Object.keys(selectedHierarchy).length > 0;

        if (!hasHierarchy) return;

        const updatedProduct = {
          ...product,
          fee: selectedHierarchy.fee,
          hierarchyId: selectedHierarchy.productHierarchyId,
          packDescription: selectedHierarchy.packDescription,
        };

        updates.push(updatedProduct);
      }
    );

    if (updates.length > 0) {
      ixoneGridApi.api.applyTransaction({
        update: updates,
      });
    }
  };
  return {
    applySelectedHierarchyForMultiProduct,
    applySelectedHierarchyForProduct,
  };
};
